import type { Session } from "@supabase/supabase-js";
import {
  holdsRole,
  type Crew,
  type Profile,
  type Trade,
} from "@roof-hawk/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { supabase, unwrap } from "./lib/supabase";

/**
 * Who is signed in, plus the reference data every screen needs.
 *
 * Loaded once at sign-in, exactly as the prototype did: staff, lead sources,
 * trades and crews are small, change rarely, and having them to hand keeps the
 * rest of the app synchronous.
 */
export interface SessionState {
  session: Session;
  /** The profile row for the signed-in user. Null if nobody linked one. */
  profile: Profile | null;
  profileId: string | null;
  displayName: string;
  /** Roles from the JWT — what RLS gates on. */
  roles: string[];
  isAdmin: boolean;
  people: Profile[];
  leadSources: string[];
  trades: Trade[];
  /** Every crew, for anywhere one can be assigned by name. */
  crews: Crew[];
  refreshReference: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession used outside SessionProvider");
  return value;
}

function rolesFromSession(session: Session): string[] {
  const metadata = session.user.app_metadata as { roles?: unknown } | undefined;
  const roles = metadata?.roles;
  return Array.isArray(roles) ? roles.map(String) : [];
}

export function SessionProvider({
  session,
  children,
}: {
  session: Session;
  children: ReactNode;
}) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [people, setPeople] = useState<Profile[]>([]);
  const [leadSources, setLeadSources] = useState<string[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshReference = useCallback(async () => {
    const [me, staff, sources, tradeRows, crewRows] = await Promise.all([
      supabase
        .from("profiles")
        .select("*")
        .eq("auth_user_id", session.user.id)
        .maybeSingle(),
      /*
       * Everybody, active or not, and `active` comes back with them.
       *
       * Deliberately not filtered here. A deactivated profile must not be drawn
       * as a chip across the top of the app — that is filtered where the chips
       * are drawn, in Shell — but it still has to be *loadable*, because jobs
       * go on pointing at whoever sold them. A screen that cannot see an
       * inactive profile cannot show who a job belongs to, and `assigneeOptions`
       * cannot honour its keep-list: an inspector deactivated after being
       * assigned would vanish from the picker and the next save would silently
       * unassign them.
       */
      supabase
        .from("profiles")
        .select(
          "id,full_name,email,roles,sort_order,is_admin,active,auth_user_id,takes_new_leads,roof_board_line,field_view",
        )
        .order("sort_order")
        .order("full_name"),
      supabase
        .from("lead_sources")
        .select("name")
        .eq("active", true)
        .order("sort_order"),
      supabase
        .from("trades")
        .select("*")
        .eq("active", true)
        .order("sort_order"),
      // Every crew, active or not, for the same reason as the profiles above:
      // `assigneeOptions` drops the inactive ones itself and keeps whoever is
      // already on the job.
      supabase.from("crews").select("*").order("sort_order").order("name"),
    ]);

    setProfile((me.data as Profile) ?? null);
    setPeople(unwrap<Profile[]>(staff));
    const sourceRows = unwrap<{ name: string }[]>(sources);
    if (sourceRows.length) setLeadSources(sourceRows.map((r) => r.name));
    setTrades(unwrap<Trade[]>(tradeRows));
    setCrews(unwrap<Crew[]>(crewRows));
  }, [session.user.id]);

  useEffect(() => {
    let alive = true;
    refreshReference()
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, [refreshReference]);

  const value = useMemo<SessionState>(() => {
    const roles = rolesFromSession(session);
    return {
      session,
      profile,
      profileId: profile?.id ?? null,
      displayName: profile?.full_name ?? session.user.email ?? "Me",
      roles,
      isAdmin: holdsRole(roles, "admin") || profile?.is_admin === true,
      people,
      leadSources,
      trades,
      crews,
      refreshReference,
      signOut: async () => {
        await supabase.auth.signOut();
        window.location.hash = "";
        window.location.reload();
      },
    };
  }, [session, profile, people, leadSources, trades, crews, refreshReference]);

  if (error) {
    return (
      <div className="wrap">
        <div className="list">
          <div className="empty">
            <b>Could not load your account</b>
            {error}
            <div style={{ marginTop: 12 }}>
              <button
                className="btn-ghost"
                onClick={() =>
                  void supabase.auth.signOut().then(() => location.reload())
                }
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="wrap">
        <div className="list">
          <div className="empty">
            <b>Loading…</b>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
